import React, { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CreditCard, Lock } from "lucide-react";
import { CardElement, useElements } from "@stripe/react-stripe-js";
import  usePaymentMethod  from "@/hooks/stripe/usePaymentMethod";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

type PaymentMethodProps = {
  form: UseFormReturn<any>;
  selectedMethod: "card" | "paypal" | "cash";
  onMethodChange: (method: "card" | "paypal" | "cash") => void;
  onPaymentMethodCreate?: (paymentMethodId: string) => void;
  existingStripePaymentMethods?: any[];
  onStripeExistingPaymentMethodSelect?: (paymentMethodId: string) => void;
};

const PaymentMethod = React.forwardRef<any, PaymentMethodProps>(
  (
    {
      form,
      selectedMethod,
      onMethodChange,
      onPaymentMethodCreate,
      existingStripePaymentMethods,
      onStripeExistingPaymentMethodSelect,
    },
    ref
  ) => {
    const elements = useElements();
    const { createPaymentMethod, loading, error } = usePaymentMethod();
    const [cardError, setCardError] = useState<string | null>(null);
    const [selectedCardOption, setSelectedCardOption] = useState<string>("new-card");


    const isCardExpired = (expMonth: number, expYear: number) => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; 
      
      if (expYear < currentYear) return true;
      if (expYear === currentYear && expMonth < currentMonth) return true;
      
      return false;
    };

    const cardElementOptions = {
      style: {
        base: {
          fontSize: "16px",
          color: "#424770",
          "::placeholder": {
            color: "#aab7c4",
          },
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontSmoothing: "antialiased",
        },
        invalid: {
          color: "#9e2146",
        },
      },
      hidePostalCode: true,
    };

    const handleCreatePaymentMethod = async (billingDetails: any) => {
      setCardError(null);

      const cardElement = elements?.getElement(CardElement);

      if (!cardElement) {
        const errorMsg = "Card element not found";
        setCardError(errorMsg);
        toast.error(errorMsg);
        return null;
      }

      try {
        const paymentMethodData = {
          type: "card" as const,
          card: cardElement,
          billing_details: billingDetails,
        };

        const result = await createPaymentMethod(paymentMethodData);

        if (result && onPaymentMethodCreate) {
          onPaymentMethodCreate(result.id);
          return result;
        } else {
          const errorMsg = "Failed to create payment method";
          setCardError(errorMsg);
          toast.error(errorMsg);
          return null;
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Unknown payment error";
        setCardError(errorMsg);
        toast.error("Payment method creation failed", {
          description: errorMsg,
        });
        return null;
      }
    };

    const handleCardChange = (event: any) => {
      if (event.error) {
        setCardError(event.error.message);
      } else {
        setCardError(null);
      }
    };

    // Enhanced expired card handling
    const handlePaymentMethodSelect = (paymentMethodId: string, paymentMethod: any) => {
      // Double-check expiration on selection
      if (isCardExpired(paymentMethod.card?.exp_month, paymentMethod.card?.exp_year)) {
        toast.error("Cannot select expired card", {
          description: "Please choose a valid payment method or add a new card",
        });
        return;
      }
      
      setSelectedCardOption(paymentMethodId);
      if (onStripeExistingPaymentMethodSelect) {
        onStripeExistingPaymentMethodSelect(paymentMethodId);
      }
    };

    React.useImperativeHandle(ref, () => ({
      createPaymentMethod: handleCreatePaymentMethod,
    }));

    return (
      <Card className="border shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>Select your preferred payment method</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          onMethodChange(value as "card" | "paypal" | "cash");
                        }}
                        className="space-y-3"
                      >
                        <div
                          className={`flex items-center space-x-2 border rounded-md p-3 ${
                            selectedMethod === "card"
                              ? "border-jmPrimary bg-gray-50"
                              : "border-gray-200"
                          }`}
                        >
                          <RadioGroupItem value="card" id="card" className="data-[state=checked]:bg-jmPrimary" />
                          <Label htmlFor="card" className="flex items-center cursor-pointer">
                            <CreditCard className="h-4 w-4 mr-2" />
                            Credit / Debit Card
                          </Label>
                        </div>

                        <div
                          className={`flex items-center space-x-2 border rounded-md p-3 ${
                            selectedMethod === "paypal"
                              ? "border-jmPrimary bg-gray-50"
                              : "border-gray-200"
                          }`}
                        >
                          <RadioGroupItem value="paypal" id="paypal" className="data-[state=checked]:bg-jmPrimary" />
                          <Label htmlFor="paypal" className="flex items-center cursor-pointer">
                            <svg
                              className="h-4 w-4 mr-2"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M19.5 8.5H18.5C18.5 5.74 16.26 3.5 13.5 3.5H7.5C6.12 3.5 5 4.62 5 6V18C5 19.1 5.9 20 7 20H13C14.1 20 15 19.1 15 18V14.5H19.5C20.88 14.5 22 13.38 22 12V11C22 9.62 20.88 8.5 19.5 8.5Z"
                                fill="#0070BA"
                              />
                              <path
                                d="M15 8.5H16.5C17.05 8.5 17.5 8.95 17.5 9.5V10.5C17.5 11.05 17.05 11.5 16.5 11.5H15V8.5Z"
                                fill="#0070BA"
                              />
                              <path
                                d="M13.5 6.5H7.5C6.95 6.5 6.5 6.95 6.5 7.5V16.5C6.5 17.05 6.95 17.5 7.5 17.5H13.5C14.05 17.5 14.5 17.05 14.5 16.5V7.5C14.5 6.95 14.05 6.5 13.5 6.5Z"
                                fill="#003087"
                              />
                            </svg>
                            PayPal
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {selectedMethod === "card" && (
                <div className="space-y-4 pt-6 border-t">
                  {/* Show existing payment methods if available */}
                  {existingStripePaymentMethods && existingStripePaymentMethods.length > 0 ? (
                    <div className="space-y-4">
                      <Label>Select Card</Label>
                      <RadioGroup
                        value={selectedCardOption}
                        onValueChange={(value) => {
                          if (value === "new-card") {
                            setSelectedCardOption(value);
                            if (onStripeExistingPaymentMethodSelect) {
                              onStripeExistingPaymentMethodSelect("");
                            }
                          } else {
                            // Find the payment method and validate
                            const paymentMethod = existingStripePaymentMethods.find(pm => pm.id === value);
                            if (paymentMethod) {
                              handlePaymentMethodSelect(value, paymentMethod);
                            }
                          }
                        }}
                        className="space-y-3"
                      >
                        {/* Existing payment methods */}
                        {existingStripePaymentMethods.map((paymentMethod: any) => {
                          const isExpired = isCardExpired(paymentMethod.card?.exp_month, paymentMethod.card?.exp_year);
                          
                          return (
                            <div
                              key={paymentMethod.id}
                              className={`flex items-center space-x-3 py-1.5 border rounded-md p-3 relative ${
                                isExpired 
                                  ? "border-red-200 bg-red-50 opacity-60 cursor-not-allowed" 
                                  : selectedCardOption === paymentMethod.id
                                    ? "border-jmPrimary bg-gray-50"
                                    : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <RadioGroupItem 
                                value={isExpired ? "disabled-" + paymentMethod.id : paymentMethod.id}
                                id={paymentMethod.id} 
                                className="data-[state=checked]:bg-jmPrimary"
                                disabled={isExpired}
                                style={{ display: isExpired ? 'none' : 'block' }} // Hide radio button completely for expired cards
                              />
                              
                              <div className="flex items-center space-x-3 flex-1">
                                {/* Card brand logo */}
                                <div className="flex-shrink-0">
                                  {/* {getCardBrandLogo(paymentMethod.card?.brand)} */}
                                </div>
                                
                                <div className="flex flex-col flex-1 space-y-1">
                                  <div className="flex items-center justify-between space-x-2">
                                    <span className={`font-medium ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                                      •••• •••• •••• {paymentMethod.card?.last4}
                                    </span>
                                    <span className={`text-sm uppercase font-medium ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                                      {paymentMethod.card?.brand}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2 justify-between">
                                    <span className={`text-sm ${isExpired ? 'text-red-600' : 'text-gray-500'}`}>
                                      Expires {String(paymentMethod.card?.exp_month).padStart(2, '0')}/{paymentMethod.card?.exp_year}
                                    </span>
                                    {paymentMethod.card?.funding && (
                                      <Badge variant="outline" className="text-xs text-gray-600">
                                        {paymentMethod.card.funding}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Expired badge */}
                                {isExpired && (
                                  <Badge variant="destructive" className="text-xs">
                                    Expired
                                  </Badge>
                                )}
                              </div>
                              
                              <Label 
                                htmlFor={paymentMethod.id} 
                                className={`absolute inset-0 cursor-pointer ${isExpired ? 'cursor-not-allowed' : ''}`}
                              />
                            </div>
                          );
                        })}

                        {/* Purchase with new card option */}
                        <div
                          className={`flex items-center space-x-2 border rounded-md p-3 ${
                            selectedCardOption === "new-card"
                              ? "border-jmPrimary bg-gray-50"
                              : "border-gray-200"
                          }`}
                        >
                          <RadioGroupItem 
                            value="new-card" 
                            id="new-card" 
                            className="data-[state=checked]:bg-jmPrimary" 
                            onClick={() => {
                              if (onStripeExistingPaymentMethodSelect) {
                                onStripeExistingPaymentMethodSelect("");
                              }
                            }}
                          />
                          <Label htmlFor="new-card" className="flex items-center cursor-pointer">
                            <CreditCard className="h-4 w-4 mr-2" />
                            Use a new card
                          </Label>
                        </div>
                      </RadioGroup>

                      {/* Show CardElement only when "new-card" is selected */}
                      {selectedCardOption === "new-card" && (
                        <div className="space-y-2 pt-4 border-t">
                          <Label>Card Details</Label>
                          <div
                            className={`border rounded-md p-3 bg-white ${
                              cardError ? "border-red-500" : ""
                            }`}
                          >
                            <CardElement options={cardElementOptions} onChange={handleCardChange} />
                          </div>
                          {(error || cardError) && (
                            <p className="text-sm text-red-500">{error || cardError}</p>
                          )}
                          {loading && (
                            <p className="text-sm text-blue-500">Processing payment method...</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Show CardElement directly when no existing payment methods */
                    <div className="space-y-2">
                      <Label>Card Details</Label>
                      <div
                        className={`border rounded-md p-3 bg-white ${
                          cardError ? "border-red-500" : ""
                        }`}
                      >
                        <CardElement options={cardElementOptions} onChange={handleCardChange} />
                      </div>
                      {(error || cardError) && (
                        <p className="text-sm text-red-500">{error || cardError}</p>
                      )}
                      {loading && (
                        <p className="text-sm text-blue-500">Processing payment method...</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center text-sm text-gray-500 mt-2">
                    <Lock className="h-3 w-3 mr-1" />
                    Your payment information is secured with SSL encryption
                  </div>
                </div>
              )}
            </div>
          </Form>
        </CardContent>
      </Card>
    );
  }
);

PaymentMethod.displayName = "PaymentMethod";

export default PaymentMethod;
